import { ReactNode, useCallback, useEffect, useState } from 'react';
import { useLocation, useSearchParams, useNavigate } from 'react-router-dom';
import { fetchPartner, verifyToken } from '../apis/partner';
import useAuthStore from '../store/useAuthStore';
import Typo from './atom/Typo';
import colors from '../constants/colors.constants';
import Spinner from './atom/Spinner';
import { getLanguageCode } from '../utils/utils';
import { fetchTreatmentCategories } from '../apis/treatment-category';

export interface AuthCheckerProps {
  children?: ReactNode;
}

function AuthChecker(props: AuthCheckerProps) {
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const navigate = useNavigate();
  const channel = searchParams.get('channel');
  const partnerId = searchParams.get('partnerId');
  const lang = searchParams.get('lang');
  const tokenQuery = searchParams.get('token');
  const isCustom = searchParams.get('isCustom');
  const [loading, setLoading] = useState(false);
  const {
    setPartner,
    setChannel,
    customer,
    clearCustomer,
    setTreatmentCategories,
  } = useAuthStore();
  const { setCustomer } = useAuthStore();
  const [invalidAccess, setInvalidAccess] = useState(!partnerId);

  const setInitialData = useCallback(async () => {
    if (channel) {
      setChannel(channel);
    }

    // 카카오 챗봇을 통해 오는 경우
    if (tokenQuery) {
      try {
        const user = await verifyToken(tokenQuery);
        const { partner, id: partnerCustomerId } = user;
        const { id } = partner;
        if (partnerId === `${id}`) {
          setCustomer({ token: tokenQuery, partnerCustomerId });
        } else {
          clearCustomer();
          removeTokenQueryAndReplace();
        }
      } catch (e) {
        clearCustomer();
        removeTokenQueryAndReplace();
        console.error('1', e);
      }
    } else if (customer) {
      try {
        const user = await verifyToken(customer.token);
        const { partner } = user;
        const { id } = partner;
        if (partnerId === `${id}`) {
          setCustomer(customer);
        } else {
          clearCustomer();
          removeTokenQueryAndReplace();
        }
      } catch (e) {
        clearCustomer();
        removeTokenQueryAndReplace();
      }
    }

    if (partnerId) {
      try {
        const langParam = lang ? getLanguageCode(lang) : undefined;
        const res = await fetchPartner(partnerId, langParam);

        const categories = await fetchTreatmentCategories({
          partnerCode: res.partnerCode,
          languageCode: langParam,
          isCustom: isCustom === 'true',
        });
        setTreatmentCategories(categories);
        setPartner(res);
      } catch (e) {
        setInvalidAccess(true);
      }
    } else {
      setInvalidAccess(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    channel,
    tokenQuery,
    partnerId,
    setChannel,
    setCustomer,
    clearCustomer,
    lang,
    setTreatmentCategories,
    setPartner,
  ]);

  useEffect(() => {
    (async function fetchData() {
      setLoading(true);
      await setInitialData();
      setLoading(false);
    })();
  }, [setInitialData]);

  const removeTokenQueryAndReplace = () => {
    const queryParams = new URLSearchParams(location.search);
    if (queryParams.has('token')) {
      console.log('REDIRECT');
      queryParams.delete('token');
      navigate(
        {
          pathname: location.pathname,
          search: queryParams.toString(),
        },
        {
          replace: true,
        },
      );
    }
  };

  if (loading) {
    return (
      <div className="app-viewport-height flex flex-col items-center justify-center">
        <Spinner />
      </div>
    );
  }

  if (invalidAccess) {
    return (
      <div className="p-4">
        <Typo type="m-16" color={colors.subTitle}>
          올바른 접근 경로가 아닙니다.
        </Typo>
      </div>
    );
  }

  const { children } = props;

  return <div>{children}</div>;
}

export default AuthChecker;
